import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { routes } from '../../routes/config';
import hamburgerIcon from '../../images/barsSolid.svg';
import closeHamburgerIcon from '../../images/timesSolid.svg';
import logo from '../../images/mobile_logo.webp';
import './style.scss';
import { SOCIAL_LINKS } from '../../constants';

const Menu: React.FC = () => {
	const [isMenuOpen, setMenuOpen] = useState<boolean>(false);

	const variants: Variants = {
		hidden: { x: '-100%' },
		visible: { x: '0%' }
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as HTMLElement;
			if (!target.closest('.navbar--mobile') && !target.closest('.navbar__toggle')) {
				setMenuOpen(false);
			}
		};

		document.addEventListener('click', handleClickOutside);

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, []);

	const toggleMenu = () => {
		setMenuOpen(!isMenuOpen);
	};

	const handleBookNow = () => {
		const phoneNumber: string = SOCIAL_LINKS.WHATSAPP.PHONE;
		const message: string = SOCIAL_LINKS.WHATSAPP.DEFAULT_MESSAGE;
		const encodedMessage: string = encodeURIComponent(message);
		const whatsappLink: string = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
		window.open(whatsappLink, '_blank');
	};

	const renderMenuItems = () => (
		<ul className="navbar__menu-list">
			{routes.map(route => (
				<li key={route.path} className="navbar__menu-list-item">
					<NavLink
						to={route.path}
						className={({ isActive }) =>
							`navbar__menu-list-text ${isActive ? 'active' : ''}`
						}
						end={route.exact}
					>
						{route.label}
					</NavLink>
				</li>
			))}
		</ul>
	);

	const floatingAnimation = {
		initial: { y: 0, scale: 1 },
		animate: {
			y: [-8, 8, -8],
			scale: [1, 1.05, 1],
			filter: [
				'brightness(1) drop-shadow(0 0 0 rgba(51, 169, 255, 0))',
				'brightness(1.3) drop-shadow(0 0 15px rgba(51, 169, 255, 0.5))',
				'brightness(1) drop-shadow(0 0 0 rgba(51, 169, 255, 0))'
			],
			transition: {
				duration: 2,
				repeat: Infinity,
				ease: 'easeInOut'
			}
		}
	};

	return (
		<header className={`navbar ${isMenuOpen ? 'navbar--mobile-open' : ''}`}>
			<section className="navbar__content">
				<button
					className="navbar__toggle"
					onClick={toggleMenu}
					aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
				>
					<img
						src={isMenuOpen ? closeHamburgerIcon : hamburgerIcon}
						alt="Menu toggle"
						className="navbar__toggle-icon"
						style={{ filter: 'drop-shadow(0 0 2px rgba(0, 0, 0, 0.8))' }}
					/>
				</button>
				<Link to="/" className="navbar__home-link">
					<img
						className="navbar__logo"
						src={logo}
						alt="Rental Hiace terbaik di Bandung, Indonesia dengan armada modern | Ketaren Transport"
					/>
				</Link>

				{/* Mobile Book Now Button */}
				<motion.div
					className="navbar__menu-book-now navbar__menu-book-now--mobile"
					initial="initial"
					animate="animate"
					variants={floatingAnimation}
					whileHover={{
						scale: 1.15,
						filter: 'brightness(1.4) drop-shadow(0 0 20px rgba(51, 169, 255, 0.7))',
						transition: {
							duration: 0.2,
							ease: 'easeOut'
						}
					}}
					whileTap={{
						scale: 0.95,
						filter: 'brightness(0.95) drop-shadow(0 0 10px rgba(51, 169, 255, 0.5))',
						transition: {
							duration: 0.1
						}
					}}
					onClick={handleBookNow}
				>
					<h4>✨ Book Now ✨</h4>
				</motion.div>

				{/* Desktop Menu */}
				<nav className="navbar__menu">{renderMenuItems()}</nav>

				{/* Mobile Menu */}
				<motion.nav
					className={`navbar--mobile ${isMenuOpen ? 'active' : ''}`}
					initial="hidden"
					animate={isMenuOpen ? 'visible' : 'hidden'}
					variants={variants}
					transition={{ duration: 0.4, ease: 'easeInOut' }}
				>
					{renderMenuItems()}
				</motion.nav>
			</section>
		</header>
	);
};

export default Menu;
